import { queryClient } from "@/lib/queryClient";
import * as Sentry from "@sentry/react";
import { ReactNode, Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { ProtectedRoute } from "./components/ProtectedRoute";
import { RootRedirect } from "./components/RootRedirect";
import { AuthenticatedLayout } from "./components/AuthenticatedLayout";
import { AuthProvider } from "./contexts/AuthContext";
import { canViewFinances } from "./lib/permissions";
import { Skeleton } from "./components/ui/skeleton";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const Login = lazyWithRetry(() => import("./pages/Login"));
const Signup = lazyWithRetry(() => import("./pages/Signup"));
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Members = lazyWithRetry(() => import("./pages/Members"));
const Activity = lazyWithRetry(() => import("./pages/Activity"));
const ExitData = lazyWithRetry(() => import("./pages/ExitData"));
const Growth = lazyWithRetry(() => import("./pages/Growth"));
const Premium = lazyWithRetry(() => import("./pages/Premium"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Broadcast = lazyWithRetry(() => import("./pages/Broadcast"));
// const Automations = lazyWithRetry(() => import("./pages/Automations"));
const Staff = lazyWithRetry(() => import("./pages/Staff"));
const Finances = lazyWithRetry(() => import("./pages/Finances"));
const Inventory = lazyWithRetry(() => import("./pages/Inventory"));
const MonthlyPerformanceReport = lazyWithRetry(
  () => import("./pages/business-intelligence/MonthlyPerformance"),
);
const DataEntry = lazyWithRetry(() => import("./pages/business-intelligence/DataEntry"));
const ClassWorkouts = lazyWithRetry(() => import("./pages/ClassWorkouts"));

const RouteFallback = () => (
  <div className="min-h-screen bg-background px-4 py-4 sm:p-6">
    <div className="mx-auto max-w-6xl space-y-6">
      <Skeleton className="h-9 w-56" />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, index) => (
          <Skeleton key={index} className="h-32 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-72 rounded-lg" />
    </div>
  </div>
);

const withRouteSuspense = (
  element: ReactNode,
  fallback: ReactNode = <RouteFallback />,
) => <Suspense fallback={fallback}>{element}</Suspense>;

const App = () => (
  <QueryClientProvider client={queryClient}>
    <Sonner />
    <BrowserRouter>
      <AuthProvider>
        <SentryRoutes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={withRouteSuspense(<Login />)} />
          <Route path="/signup" element={withRouteSuspense(<Signup />)} />
          <Route
            path="/forgot-password"
            element={withRouteSuspense(<ForgotPassword />)}
          />
          <Route
            path="/reset-password"
            element={withRouteSuspense(<ResetPassword />)}
          />
          <Route element={<AuthenticatedLayout />}>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/members"
              element={
                <ProtectedRoute>
                  <Members />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wallet-transactions"
              element={
                <ProtectedRoute canAccess={canViewFinances}>
                  <Finances />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finances"
              element={<Navigate to="/wallet-transactions" replace />}
            />
            <Route
              path="/activity"
              element={
                <ProtectedRoute>
                  <Activity />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute>
                  <Inventory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/growth"
              element={
                <ProtectedRoute>
                  <Growth />
                </ProtectedRoute>
              }
            />
            <Route
              path="/premium"
              element={
                <ProtectedRoute>
                  <Premium />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-intelligence/mpr"
              element={
                <ProtectedRoute canAccess={canViewFinances}>
                  <MonthlyPerformanceReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-intelligence/data-entry"
              element={
                <ProtectedRoute>
                  <DataEntry />
                </ProtectedRoute>
              }
            />
            <Route
              path="/broadcast"
              element={
                <ProtectedRoute>
                  <Broadcast />
                </ProtectedRoute>
              }
            />
            <Route
              path="/communications"
              element={
                <ProtectedRoute>
                  <Broadcast />
                </ProtectedRoute>
              }
            />
            <Route
              path="/communications/class-schedules/:classScheduleId/workouts"
              element={
                <ProtectedRoute>
                  <ClassWorkouts />
                </ProtectedRoute>
              }
            />
            {/* Automations routes are hidden while the feature is unused.
            <Route
              path="/automations"
              element={
                <ProtectedRoute>
                  <Automations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/automations/email"
              element={
                <ProtectedRoute>
                  <Automations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/automations/member-mobile-app"
              element={
                <ProtectedRoute>
                  <Automations />
                </ProtectedRoute>
              }
            />
            */}
            <Route
              path="/exit-data"
              element={
                <ProtectedRoute>
                  <ExitData />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff"
              element={
                <ProtectedRoute>
                  <Staff />
                </ProtectedRoute>
              }
            />
            <Route path="/settings" element={<Settings />} />
          </Route>
          <Route path="*" element={withRouteSuspense(<NotFound />)} />
        </SentryRoutes>
      </AuthProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
